import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Archive, Film, ExternalLink } from "lucide-react";

export default function AdminArchives() {
  const { t, ml, money } = useLang();
  const [groups, setGroups] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/archives").then((r) => setGroups(r.data)).catch(() => {});
  }, []);

  return (
    <div className="ch-fade-up space-y-8">
      <header>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-neutral-400">{t("arch.eyebrow")}</p>
        <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight">{t("nav.archives")}</h1>
        <p className="mt-2 text-sm text-neutral-500">{t("arch.subtitle_admin")}</p>
      </header>

      {groups.length === 0 && (
        <div className="flex flex-col items-center gap-3 rounded-sm border border-dashed border-neutral-200 py-16 text-neutral-400">
          <Archive className="h-8 w-8" strokeWidth={1.5} />
          <p className="text-sm">{t("arch.empty_admin")}</p>
        </div>
      )}

      <Accordion type="multiple" className="space-y-3">
        {groups.map((g) => (
          <AccordionItem key={g.key} value={g.key} data-testid={`archive-group-${g.key}`} className="rounded-sm border border-neutral-200 px-5">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex w-full items-center justify-between pr-4">
                <span className="font-display text-lg font-bold">{ml(g.month, g.year)}</span>
                <span className="text-sm font-normal text-neutral-500">{g.orders.length} {g.orders.length > 1 ? t("common.orders") : t("common.order")} · {money(g.revenue)}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-2 pb-2">
                {g.orders.map((o) => (
                  <button key={o.id} data-testid={`archive-order-${o.id}`} onClick={() => setActive(o)}
                    className="flex w-full items-center justify-between rounded-sm border border-neutral-100 px-4 py-3 text-left transition-colors hover:bg-neutral-50">
                    <div>
                      <p className="font-semibold">{o.client_name}</p>
                      <p className="flex items-center gap-1.5 text-sm text-neutral-500"><Film className="h-3.5 w-3.5" />{o.package_name} · {o.videos_used} {t("arch.videos_delivered")}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <StatusBadge status={o.status} />
                      <span className="font-display font-bold">{money(o.price)}</span>
                    </div>
                  </button>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent data-testid="archive-detail-dialog" className="max-w-lg rounded-sm">
          {active && (
            <>
              <DialogHeader><DialogTitle className="font-display text-2xl">{active.client_name}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center justify-between rounded-sm bg-black p-4 text-white">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-white/50">{active.package_name}</p>
                    <p className="mt-1 text-sm text-white/70">{active.videos_used}/{active.video_count} {t("common.videos")} · {money(active.price)}</p>
                  </div>
                  <StatusBadge status={active.status} />
                </div>
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400">{t("ao.video_links")}</p>
                  {active.videos.length === 0 && <p className="text-sm text-neutral-400">{t("ao.no_videos")}</p>}
                  {active.videos.map((v, i) => (
                    <a key={i} href={v.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-sm border border-neutral-200 px-3 py-2 text-sm hover:bg-neutral-50">
                      <ExternalLink className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{v.title || v.url}</span>
                    </a>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
